import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download, Filter, RefreshCw, BarChart3, PieChart,
  TrendingUp, Users, Building, MapPin, CreditCard,
  Calendar, FileSpreadsheet, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExportStatistiquesAvance({ getAuthHeader }) {
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  
  // Filtres
  const [filters, setFilters] = useState({
    date_debut: '',
    date_fin: '',
    type_paiement: '',
    territoire_id: '',
    cooper_id: '',
    statut: ''
  });

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/superadmin/export/filters/options`,
          { headers: getAuthHeader() }
        );
        setFilterOptions(response.data);
      } catch {
        toast.error('Erreur lors du chargement des options de filtres');
      }
    };
    loadOptions();
  }, [getAuthHeader]);

  const loadStatistics = async () => {
    setLoadingStats(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(
        `${BACKEND_URL}/api/superadmin/export/statistics?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setStatistics(response.data);
      toast.success('Statistiques chargées avec succès');
    } catch {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoadingStats(false);
    }
  };

  const exportCSV = async (type) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(
        `${BACKEND_URL}/api/superadmin/export/csv/${type}?${params.toString()}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_${type}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export ${type} téléchargé avec succès`);
    } catch {
      toast.error(`Erreur lors de l'export ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      date_debut: '',
      date_fin: '',
      type_paiement: '',
      territoire_id: '',
      cooper_id: '',
      statut: ''
    });
    setStatistics(null);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Export & Statistiques Avancées</h2>
          <p className="text-gray-500">Exportez vos données avec des filtres avancés et visualisez les statistiques</p>
        </div>
        <Button onClick={resetFilters} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Réinitialiser
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-blue-600" />
            Filtres
          </CardTitle>
          <CardDescription>Définissez vos critères de filtrage pour l&apos;export et les statistiques</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Période */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date début
              </Label>
              <DatePickerFr
                className="w-full"
                value={filters.date_debut}
                onChange={(v) => setFilters({...filters, date_debut: v})}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date fin
              </Label>
              <DatePickerFr
                className="w-full"
                value={filters.date_fin}
                onChange={(v) => setFilters({...filters, date_fin: v})}
              />
            </div>

            {/* Type de paiement */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Type de paiement
              </Label>
              <Select 
                value={filters.type_paiement || "all"} 
                onValueChange={(value) => setFilters({...filters, type_paiement: value === "all" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {filterOptions?.types_paiement?.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Territoire */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Territoire
              </Label>
              <Select 
                value={filters.territoire_id || "all"} 
                onValueChange={(value) => setFilters({...filters, territoire_id: value === "all" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les territoires" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les territoires</SelectItem>
                  {filterOptions?.territoires?.map((terr) => (
                    <SelectItem key={terr.value} value={terr.value}>
                      {terr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* COOPER'S */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                COOPER&apos;S
              </Label>
              <Select 
                value={filters.cooper_id || "all"} 
                onValueChange={(value) => setFilters({...filters, cooper_id: value === "all" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les COOPER'S" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les COOPER&apos;S</SelectItem>
                  {filterOptions?.coopers?.map((cooper) => (
                    <SelectItem key={cooper.value} value={cooper.value}>
                      {cooper.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Statut */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Statut
              </Label>
              <Select 
                value={filters.statut || "all"} 
                onValueChange={(value) => setFilters({...filters, statut: value === "all" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {filterOptions?.statuts?.map((statut) => (
                    <SelectItem key={statut.value} value={statut.value}>
                      {statut.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3 mt-6">
            <Button onClick={loadStatistics} disabled={loadingStats} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {loadingStats ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              Voir les statistiques
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques */}
      {statistics && (
        <>
          {/* Résumé */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Achats</p>
                    <p className="text-3xl font-bold text-blue-800">{statistics.resume?.total_achats || 0}</p>
                  </div>
                  <CreditCard className="h-10 w-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Montant Total</p>
                    <p className="text-3xl font-bold text-green-800">{statistics.resume?.montant_total?.toFixed(2) || 0} €</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Souscriptions Actives</p>
                    <p className="text-3xl font-bold text-purple-800">{statistics.souscriptions?.actives || 0}</p>
                  </div>
                  <Users className="h-10 w-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">COOPER&apos;S</p>
                    <p className="text-3xl font-bold text-orange-800">{statistics.utilisateurs?.coopers || 0}</p>
                  </div>
                  <Building className="h-10 w-10 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Statistiques par type de paiement */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-indigo-600" />
                  Par Type de Paiement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🏦</span>
                      <div>
                        <p className="font-medium">Virement</p>
                        <p className="text-sm text-gray-500">{statistics.par_type_paiement?.virement?.count || 0} transactions</p>
                      </div>
                    </div>
                    <Badge className="bg-blue-600 text-white font-semibold">
                      {statistics.par_type_paiement?.virement?.montant_total?.toFixed(2) || 0} €
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">💳</span>
                      <div>
                        <p className="font-medium">Carte Bancaire</p>
                        <p className="text-sm text-gray-500">{statistics.par_type_paiement?.carte_bancaire?.count || 0} transactions</p>
                      </div>
                    </div>
                    <Badge className="bg-purple-600 text-white font-semibold">
                      {statistics.par_type_paiement?.carte_bancaire?.montant_total?.toFixed(2) || 0} €
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Par statut */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-green-600" />
                  Par Statut
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(statistics.par_statut || {}).map(([statut, data]) => (
                    <div key={statut} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{statut.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-500">{data.count} achats</p>
                      </div>
                      <Badge variant="outline">
                        {data.montant_total?.toFixed(2) || 0} €
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top COOPER'S */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-orange-600" />
                Top COOPER&apos;S par Chiffre d&apos;Affaires
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">COOPER&apos;S</th>
                      <th className="text-center py-3 px-4">Nb Achats</th>
                      <th className="text-center py-3 px-4">Virements</th>
                      <th className="text-center py-3 px-4">CB</th>
                      <th className="text-right py-3 px-4">Montant Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(statistics.par_cooper || []).slice(0, 10).map((cooper, idx) => (
                      <tr key={cooper.cooper_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="w-6 h-6 flex items-center justify-center text-xs">
                              {idx + 1}
                            </Badge>
                            <span className="font-medium">{cooper.entreprise}</span>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">{cooper.nb_achats}</td>
                        <td className="text-center py-3 px-4">
                          <Badge className="bg-blue-700 text-white font-semibold">{cooper.achats_virement}</Badge>
                        </td>
                        <td className="text-center py-3 px-4">
                          <Badge className="bg-purple-700 text-white font-semibold">{cooper.achats_cb}</Badge>
                        </td>
                        <td className="text-right py-3 px-4 font-semibold text-green-600">
                          {cooper.montant_total?.toFixed(2)} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Par Territoire */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-600" />
                Par Territoire
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(statistics.par_territoire || []).map((terr) => (
                  <div key={terr.territoire_id} className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border">
                    <h4 className="font-semibold text-gray-900">{terr.nom}</h4>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{terr.nb_coopers}</span> COOPER&apos;S
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{terr.nb_achats}</span> achats
                      </p>
                      <p className="text-lg font-bold text-green-600">
                        {terr.montant_total?.toFixed(2)} €
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Boutons d'export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Export CSV
          </CardTitle>
          <CardDescription>Téléchargez les données filtrées au format CSV</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              onClick={() => exportCSV('achats')} 
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 h-auto py-4"
            >
              <div className="flex flex-col items-center gap-2">
                <Download className="h-6 w-6" />
                <span>Exporter Achats</span>
                <span className="text-xs opacity-75">Leads achetés</span>
              </div>
            </Button>
            
            <Button 
              onClick={() => exportCSV('souscriptions')} 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 h-auto py-4"
            >
              <div className="flex flex-col items-center gap-2">
                <Download className="h-6 w-6" />
                <span>Exporter Souscriptions</span>
                <span className="text-xs opacity-75">Contrats actifs</span>
              </div>
            </Button>
            
            <Button 
              onClick={() => exportCSV('utilisateurs')} 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 h-auto py-4"
            >
              <div className="flex flex-col items-center gap-2">
                <Download className="h-6 w-6" />
                <span>Exporter Utilisateurs</span>
                <span className="text-xs opacity-75">Tous les profils</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
